/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Apr 04 2024
 **/

import { EventID, ModalID } from "@/frameworks/core/commons";
import { useModal } from "@/frameworks/designStuff/components";
import { EventHandler } from "@rfice/eventhandler";
import { useChatMessageActions } from "@/frameworks/core/chat";
import { useChatDataStore } from "@/modules/chat/store";
import { NoticeConfirmModal } from "../components";

type User = WebServices.Chat.Messages.Data["content"][number]["user"];

export default function useSubmenuActions() {
	const { deleteMessage: deleteMessageCommand, registerNotice } = useChatMessageActions();
	const modal = useModal();

	const replyMessage = ({
		messageID,
		files,
		message,
		user,
		roomID
	}: {
		messageID: string;
		files?: ChatServices.Server.MessageSent.Data["files"] | undefined;
		message: ChatServices.Format2.Container.Richtext | ChatServices.Format2.Container.Vote;
		user: User;
		roomID: string;
	}) => {
		useChatDataStore.getState().change_replying_state(roomID, true);

		EventHandler.emit(EventID.REPLY_CHAT, {
			messageID,
			files,
			message,
			user,
			roomID
		});
	};

	const noticeMessage = ({ roomID, spaceID, messageID }: { roomID: string; spaceID?: string; messageID: string }) => {
		modal.show({
			id: ModalID.ALERT_CHAT_NOTIFICATION,
			content: (
				<NoticeConfirmModal
					roomID={roomID}
					spaceID={spaceID}
					onSubmit={() => {
						registerNotice({
							channelID: roomID,
							messageID
						});
						modal.hide(ModalID.ALERT_CHAT_NOTIFICATION);
					}}
					onCancel={() => {
						modal.hide(ModalID.ALERT_CHAT_NOTIFICATION);
					}}
				/>
			)
		});
	};

	const deleteMessage = async ({ roomID, messageID }: { messageID: string; roomID: string }) => {
		return await deleteMessageCommand({
			targetMessageID: messageID,
			channelID: roomID
		});
	};

	return { replyMessage, noticeMessage, deleteMessage };
}
